import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import { CommunityMember, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';
import { v4 as uuidv4 } from 'uuid';

interface CommunityMembersManagementProps {
  permissions: CrudPermissions;
}

export const CommunityMembersManagement: React.FC<CommunityMembersManagementProps> = ({ permissions }) => {
  const { communityMembers: members, setCommunityMembers: setMembers } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<CommunityMember | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<CommunityMember>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const matchesSearch =
        member.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.phone.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [members, searchQuery]);

  const handleAdd = () => {
    setEditingMember(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
    setOpenDialog(true);
  };

  const handleEdit = (member: CommunityMember) => {
    setEditingMember(member);
    setFormData(member);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this community member?')) {
      setMembers(members.filter((m) => m.id !== id));
      const index = mockCommunityMembers.findIndex((m) => m.id === id);
      if (index > -1) {
        mockCommunityMembers.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (editingMember) {
      const updated = members.map((m) =>
        m.id === editingMember.id ? { ...m, ...formData } : m
      );
      setMembers(updated);
      const index = mockCommunityMembers.findIndex((m) => m.id === editingMember.id);
      if (index > -1) {
        mockCommunityMembers[index] = { ...mockCommunityMembers[index], ...formData } as CommunityMember;
      }
    } else {
      const newMember: CommunityMember = {
        id: uuidv4(),
        firstName: formData.firstName!,
        lastName: formData.lastName!,
        email: formData.email!,
        phone: formData.phone!,
      };
      setMembers([...members, newMember]);
      mockCommunityMembers.push(newMember);
    }
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Community Members Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Community Member
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search community members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {filteredMembers.map((member) => (
          <Box
            key={member.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <CardContent sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '100%',
              }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 2,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.firstName} {member.lastName}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.email}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.phone}
                </Typography>
              </CardContent>

              {(permissions.update || permissions.delete) && (
                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                  {permissions.update && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleEdit(member)}
                      sx={{ flexGrow: 1, minWidth: 0 }}
                    >
                      Edit
                    </Button>
                  )}
                  {permissions.delete && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(member.id)}
                      color="error"
                      sx={{ flexShrink: 0 }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </CardActions>
              )}
            </Card>
          </Box>
        ))}
      </Box>

      {filteredMembers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No community members found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Community Member" to create your first community member'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Community Member' : 'Add New Community Member'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
